import { useEffect } from 'react'
import { Redirect, Route, Switch, useLocation, useSearch } from 'wouter'
import type {
  AuthProfile,
  CharacterSummary,
  CreationEdit,
  Locale,
} from '@rollcall/contracts'
import type { Api } from '../api.js'
import type { Session } from '../auth/session.js'
import {
  applyTheme,
  initialTheme,
  storeTheme,
  type Theme,
  type ThemeStorage,
} from '../theme.js'
import { readStoredLanguage, storeLanguage, type LanguageStorage } from '../language.js'
import type { Language } from '../i18n.js'
import { screenTitle } from '../heading.js'
import { useState } from 'react'
import {
  ROUTES,
  aboutPath,
  whatsNewPath,
  campaignPath,
  charactersPath,
  contentPath,
  contentRecordPath,
  forgotPasswordPath,
  newContentPath,
  homePath,
  joinPath,
  isTab,
  newCharacterPath,
  sheetPath,
  editCharacterPath,
  printCharacterPath,
  signInPath,
  signUpPath,
  withSearch,
} from '../routes.js'
import { DEFAULT_TAB, type TabKey } from '../tabs.js'
import { appBuildInfo } from '../build-info.js'
import { AboutScreen } from './about.js'
import { WhatsNewScreen } from './whats-new.js'
import {
  AdoptDraftsPrompt,
  ConfirmEmailScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  SignInScreen,
  SignUpScreen,
} from './auth.js'
import { CampaignListScreen, CampaignScreen } from './campaigns.js'
import { ContentEditorScreen, ContentListScreen } from './content.js'
import { HomeScreen } from './home.js'
import { AppMenu } from './menu.js'
import { JoinScreen } from './join.js'
import { PrintScreen } from './print-sheet.js'
import { CharacterList, CharacterSheetView } from './characters.js'
import { useT } from './i18n-context.js'
import { StartScreen } from './start.js'
import { TabBar } from './tab-bar.js'
import { CreationWizard } from './wizard.js'

/**
 * Каркас приложения (D10) и маршрутизация (D27).
 *
 * Раскладка одна: телефон — основной размер, на десктопе тот же
 * интерфейс стоит центральной колонкой. Отдельной десктопной вёрстки
 * нет и не планируется — две раскладки означают две правки на каждое
 * изменение.
 *
 * Экран определяется адресом, а не состоянием компонента. Поэтому
 * прямая ссылка на лист открывает лист, кнопка «назад» работает
 * сама, а ссылку на вкладку можно прислать в чат.
 *
 * Вкладки листа показываются только на самом листе. Создание
 * персонажа — отдельный экран со своим потоком: вкладки там означали
 * бы, что из середины создания можно уйти в «Бой», а уходить оттуда
 * некуда — персонажа ещё нет.
 *
 * Тема переживает перезагрузку и лежит в локальном хранилище, потому
 * что профиля пользователя ещё нет (он появится с регистрацией,
 * фаза 3). Вкладка не хранится нигде: она в адресе.
 */

export type AppProps = {
  readonly api: Api
  /** Хранилище выбора темы и языка: оба переживают перезагрузку. */
  readonly storage: ThemeStorage & LanguageStorage
  readonly view: Pick<Window, 'matchMedia'>
  readonly root: Pick<HTMLElement, 'setAttribute'>
  /** Кампания и место игрока: до аутентификации приходят снаружи. */
  readonly campaignId: string | null
  readonly seatId: string | null
  /** Стартовый уровень кампании, за стол которой идут (D60). */
  readonly startLevel: number | null
  /** Сессия мастера (D8): кто вошёл и чем это подтверждено. */
  readonly session: Session
  /**
   * Язык, с которого начинается сеанс (D11): выбор человека, если он
   * был, иначе язык браузера. Он же уходит в профиль при регистрации:
   * письма приходят на языке, на котором человек регистрировался.
   */
  readonly locale: Locale
  /**
   * Сменить язык по-настоящему: подменить словарь `i18next`
   * и отрисовать заново. Живёт в точке входа, а не здесь, — как
   * и всё остальное от настоящего браузера (main.tsx).
   */
  readonly onLanguage: (language: Language) => void
  /**
   * Фрагмент адреса: в нём приходит токен из письма. Свойством,
   * а не чтением `location` внутри, — как и всё остальное от
   * настоящего браузера (main.tsx).
   */
  readonly hash: string
}

export type Heading = {
  readonly title: string
  readonly subtitle: string | null
}

export function App({
  api,
  storage,
  view,
  root,
  campaignId,
  seatId,
  startLevel,
  session,
  locale,
  onLanguage,
  hash,
}: AppProps) {
  const t = useT()
  const [profile, setProfile] = useState<AuthProfile | null>(() => session.profile())
  // Драфты, которые предложено перенести на аккаунт (D33). Пока список
  // не пуст, поверх всего стоит вопрос: спросить нужно один раз
  // и в тот момент, когда ответ человеку очевиден.
  const [adoptable, setAdoptable] = useState<readonly CharacterSummary[]>([])

  useEffect(() => session.subscribe(setProfile), [session])
  const [theme, setTheme] = useState<Theme>(() => initialTheme(storage, view))
  const [language, setLanguage] = useState<Language>(locale)
  const [location, navigate] = useLocation()
  const search = useSearch()
  /**
   * Докуда доводить только что созданного персонажа повышениями (D60).
   *
   * Кампания стартует с третьего уровня, визард создаёт первый —
   * дальше идут экраны повышения подряд. Цель держится состоянием,
   * а не адресом: это намерение игрока на ближайшие пять минут,
   * а не свойство персонажа. Перезагрузил страницу — персонаж
   * остался на достигнутом уровне и доберёт обычным способом.
   */
  const [buildTo, setBuildTo] = useState<number | null>(null)
  /**
   * Откуда вошли в «О приложении».
   *
   * Экран открывается кнопкой из шапки, а шапка стоит везде: с листа,
   * из кампании, из визарда. Возврат «всегда к списку персонажей»
   * выбрасывал мастера из кампании, а игрока — с открытого листа.
   * Прямой заход по адресу истории не оставляет — тогда список.
   */
  const [aboutFrom, setAboutFrom] = useState<string | null>(null)
  // Кто открыт: шапка называет персонажа, а не экран. Заголовок
  // приходит снизу, от листа: только он знает, чей это лист,
  // и знает это после ответа сервера, а не до него.
  const [heading, setHeading] = useState<Heading | null>(null)
  /**
   * Фрагмент адреса: в нём приходит токен (письма, приглашение D51).
   *
   * Приходит он свойством при первой отрисовке, но меняться умеет
   * и внутри приложения: ссылку от мастера вставляют на экране входа,
   * и переход туда обязан донести токен. Переход `pushState`
   * события `hashchange` не даёт, поэтому фрагмент едет состоянием,
   * а не читается из адреса второй раз.
   */
  const [fragment, setFragment] = useState(hash)

  useEffect(() => {
    applyTheme(root, theme)
  }, [root, theme])

  /**
   * Язык профиля мастера (D11).
   *
   * Подхватывается только тогда, когда своего выбора человек ещё
   * не делал: нажатие на переключатель сильнее профиля. Иначе кнопка
   * молча отменяла бы саму себя на каждом восстановлении сессии.
   *
   * Сессия восстанавливается после первой отрисовки (D35), поэтому
   * это эффект, а не начальное значение: профиля на старте ещё нет.
   */
  useEffect(() => {
    if (profile === null) return
    if (readStoredLanguage(storage) !== null) return
    if (profile.locale === language) return
    setLanguage(profile.locale)
    onLanguage(profile.locale)
  }, [profile, storage, language, onLanguage])

  const chooseTheme = (next: Theme) => {
    setTheme(next)
    storeTheme(storage, next)
  }

  /**
   * Смена языка (D11).
   *
   * Выбор запоминается здесь, а подменяет словарь точка входа:
   * `i18next` живёт снаружи компонентов, как и хранилище с адресной
   * строкой. Компоненту остаётся сказать, что выбрали.
   */
  const chooseLanguage = (next: Language) => {
    setLanguage(next)
    storeLanguage(storage, next)
    onLanguage(next)
  }

  // Переход всегда с текущей строкой запроса: на ней держится вход
  // по приглашению (D8), и терять её нельзя ни на одном переходе.
  const go = (path: string) => {
    navigate(withSearch(path, search))
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-heading">
          {/* Шапка называет то, что под ней: персонажа на листе,
              экран — на всех остальных. «Лист персонажа» стояло
              и на входе, и в списке: верно везде и бесполезно нигде. */}
          <h1 className="app-title">{heading?.title ?? screenTitle(t, location)}</h1>
          {heading?.subtitle !== undefined && heading.subtitle !== null && (
            <p className="app-subtitle" data-testid="header-subtitle">
              {heading.subtitle}
            </p>
          )}
        </div>
        {/* В шапке одна кнопка. Раньше их было пять — вход, кампании,
            «о приложении», язык, тема, — и на телефоне они отжимали
            заголовок в две строки, а половину из них человек искал
            глазами каждый раз заново. Всё это теперь за меню. */}
        <AppMenu
          signedIn={profile !== null}
          email={profile?.email ?? null}
          theme={theme}
          language={language}
          onHome={() => {
            go(homePath())
          }}
          onContent={() => {
            go(contentPath())
          }}
          onAbout={() => {
            if (location !== ROUTES.about) setAboutFrom(location)
            go(aboutPath())
          }}
          onWhatsNew={() => {
            if (location !== ROUTES.whatsNew) setAboutFrom(location)
            go(whatsNewPath())
          }}
          onSignIn={() => {
            go(signInPath())
          }}
          onSignOut={() => {
            void session.signOut(false).then(() => {
              go(charactersPath())
            })
          }}
          onTheme={chooseTheme}
          onLanguage={chooseLanguage}
        />
      </header>

      <main className="app-main">
        {adoptable.length > 0 ? (
          <AdoptDraftsPrompt
            api={api}
            drafts={adoptable}
            onDone={() => {
              setAdoptable([])
              go(homePath())
            }}
          />
        ) : (
          <Switch>
            <Route path={ROUTES.signIn}>
              <SignInScreen
                api={api}
                session={session}
                onSignedIn={(drafts) => {
                  // Драфтов нет — спрашивать нечего, сразу на главную:
                  // оттуда видно и персонажей, и кампании.
                  if (drafts.length === 0) go(homePath())
                  else setAdoptable(drafts)
                }}
                onSignUp={() => {
                  go(signUpPath())
                }}
                onForgot={() => {
                  go(forgotPasswordPath())
                }}
              />
            </Route>

            <Route path={ROUTES.signUp}>
              <SignUpScreen
                api={api}
                locale={locale}
                onSignIn={() => {
                  go(signInPath())
                }}
              />
            </Route>

            <Route path={ROUTES.forgotPassword}>
              <ForgotPasswordScreen
                api={api}
                onSignIn={() => {
                  go(signInPath())
                }}
              />
            </Route>

            <Route path={ROUTES.confirmEmail}>
              <ConfirmEmailScreen
                api={api}
                hash={fragment}
                onSignIn={() => {
                  go(signInPath())
                }}
              />
            </Route>

            <Route path={ROUTES.resetPassword}>
              <ResetPasswordScreen
                api={api}
                hash={fragment}
                onSignIn={() => {
                  go(signInPath())
                }}
              />
            </Route>

            {/* Корень — развилка (D29). С параметрами кампании выбирать
              нечего: место известно, и лишний экран между ссылкой
              и списком только мешает. Без них — экран входа. */}
            <Route path={ROUTES.start}>
              {campaignId !== null ? (
                <Redirect to={withSearch(charactersPath(), search)} replace />
              ) : profile !== null ? (
                // Сессия пережила перезагрузку (D35): человек с
                // аккаунтом открывает приложение и попадает туда же,
                // куда после входа, — на главную, а не на развилку
                // «создать персонажа или войти по ссылке» (D29).
                <Redirect to={withSearch(homePath(), search)} replace />
              ) : (
                <StartScreen
                  api={api}
                  onCreate={() => {
                    go(newCharacterPath())
                  }}
                  onJoin={(token) => {
                    // Дальше — обычный путь по ссылке: экран
                    // приглашения с токеном во фрагменте (D51, D55).
                    const next = `#token=${encodeURIComponent(token)}`
                    setFragment(next)
                    navigate(`${joinPath()}${next}`)
                  }}
                  onList={() => {
                    go(charactersPath())
                  }}
                />
              )}
            </Route>

            {/* Главная аккаунта: оба раздела рядом (D8). Гостю она
              не положена — у него нет аккаунта, и его путь остаётся
              прежним (D29): список персонажей этого устройства. */}
            <Route path={ROUTES.home}>
              {profile === null ? (
                <Redirect to={withSearch(charactersPath(), search)} replace />
              ) : (
                <HomeScreen
                  api={api}
                  onCreateCharacter={() => {
                    go(newCharacterPath())
                  }}
                  onOpenCharacter={(character) => {
                    go(sheetPath(character.id, DEFAULT_TAB))
                  }}
                  onOpenCampaign={(campaign) => {
                    go(campaignPath(campaign.id))
                  }}
                />
              )}
            </Route>

            {/* Свои записи контента (D53, D62). Заводит их и мастер,
              и игрок без аккаунта: у игрока записи привязаны к его
              устройству и за столом работают после подтверждения. */}
            <Route path={ROUTES.content}>
              <ContentListScreen
                api={api}
                onNew={() => {
                  go(newContentPath())
                }}
                onEdit={(item) => {
                  go(contentRecordPath(item.id))
                }}
              />
            </Route>

            <Route path={ROUTES.newContent}>
              <ContentEditorScreen
                api={api}
                recordId={null}
                seatId={seatId}
                onDone={() => {
                  go(contentPath())
                }}
                onCancel={() => {
                  go(contentPath())
                }}
              />
            </Route>

            <Route path={ROUTES.contentRecord}>
              {(params) => (
                <ContentEditorScreen
                  api={api}
                  recordId={params['id'] ?? null}
                  seatId={seatId}
                  onDone={() => {
                    go(contentPath())
                  }}
                  onCancel={() => {
                    go(contentPath())
                  }}
                />
              )}
            </Route>

            {/* Кампании мастера (D8). */}
            <Route path={ROUTES.campaigns}>
              <CampaignListScreen
                api={api}
                onOpen={(campaign) => {
                  go(campaignPath(campaign.id))
                }}
              />
            </Route>

            <Route path={ROUTES.campaign}>
              {(params) => (
                <CampaignScreen
                  api={api}
                  campaignId={params['id'] ?? ''}
                  // Шапка называет кампанию её именем: «Лист
                  // персонажа» над столом было неверно вдвойне.
                  onHeading={setHeading}
                  onContent={() => {
                    go(contentPath())
                  }}
                />
              )}
            </Route>

            {/* Приход игрока по ссылке (D38). Аккаунта у него нет,
              и не требуется: удостоверяет его токен устройства. */}
            <Route path={ROUTES.join}>
              <JoinScreen
                api={api}
                hash={fragment}
                onOpenCharacter={(characterId) => {
                  go(sheetPath(characterId, DEFAULT_TAB))
                }}
                onCreateCharacter={(seat, level) => {
                  // Место известно: визард создаёт персонажа сразу
                  // за столом, а не драфтом. Уровень кампании идёт
                  // тем же путём — умолчанием поля сборки (D60).
                  navigate(
                    `${newCharacterPath()}?seat=${encodeURIComponent(seat)}` +
                      `&level=${String(level)}`,
                  )
                }}
              />
            </Route>

            <Route path={ROUTES.whatsNew}>
              <WhatsNewScreen
                onBack={() => {
                  go(aboutFrom ?? charactersPath())
                  setAboutFrom(null)
                }}
              />
            </Route>

            <Route path={ROUTES.about}>
              <AboutScreen
                build={appBuildInfo()}
                onBack={() => {
                  go(aboutFrom ?? charactersPath())
                  setAboutFrom(null)
                }}
              />
            </Route>

            <Route path={ROUTES.characters}>
              <CharacterList
                api={api}
                campaignId={campaignId}
                onCreate={() => {
                  go(newCharacterPath())
                }}
                onOpen={(character) => {
                  go(sheetPath(character.id, DEFAULT_TAB))
                }}
              />
            </Route>

            <Route path={ROUTES.newCharacter}>
              <CreationWizard
                api={api}
                seatId={seatId}
                campaignLevel={startLevel ?? 1}
                onCreated={(character, target) => {
                  // Цель сборки живёт здесь, а не в адресе: это
                  // намерение на ближайшие пять минут, а не свойство
                  // персонажа (D60). Ушёл со страницы — персонаж
                  // остался на достигнутом уровне.
                  setBuildTo(target > 1 ? target : null)
                  go(sheetPath(character.id, DEFAULT_TAB))
                }}
                onCancel={() => {
                  go(charactersPath())
                }}
              />
            </Route>

            <Route path={ROUTES.editCharacter}>
              {(params) => (
                <EditScreen
                  api={api}
                  characterId={params['id'] ?? ''}
                  onDone={() => {
                    go(sheetPath(params['id'] ?? '', DEFAULT_TAB))
                  }}
                />
              )}
            </Route>

            <Route path={ROUTES.printCharacter}>
              {(params) => (
                <PrintScreen
                  api={api}
                  characterId={params['id'] ?? ''}
                  onHeading={setHeading}
                  onBack={() => {
                    go(sheetPath(params['id'] ?? '', DEFAULT_TAB))
                  }}
                />
              )}
            </Route>

            {/* Лист без вкладки — адрес, который человек напишет руками:
              он получает вкладку по умолчанию, а не пустой экран. */}
            <Route path={ROUTES.sheet}>
              {(params) => (
                <Redirect
                  to={withSearch(sheetPath(params['id'] ?? '', DEFAULT_TAB), search)}
                  replace
                />
              )}
            </Route>

            <Route path={ROUTES.sheetTab}>
              {(params) =>
                isTab(params['tab']) ? (
                  <SheetScreen
                    api={api}
                    characterId={params['id'] ?? ''}
                    tab={params['tab']}
                    // Скрытый бросок — право мастера (D12). Аккаунт есть
                    // только у него: игрок пользователем не является (D8).
                    signedIn={profile !== null}
                    buildTo={buildTo}
                    onHeading={setHeading}
                    onEditChoices={() => {
                      go(editCharacterPath(params['id'] ?? ''))
                    }}
                    onPrint={() => {
                      go(printCharacterPath(params['id'] ?? ''))
                    }}
                    // Кнопка называет список персонажей — туда
                    // и ведёт. На главную ведёт меню, и подменять
                    // одно другим значит соврать в подписи.
                    onBack={() => {
                      go(charactersPath())
                    }}
                  />
                ) : (
                  <UnknownRoute
                    onHome={() => {
                      go(charactersPath())
                    }}
                  />
                )
              }
            </Route>

            <Route>
              <UnknownRoute
                onHome={() => {
                  go(charactersPath())
                }}
              />
            </Route>
          </Switch>
        )}
      </main>

      {/* Вкладки — часть листа, поэтому и живут только на нём. */}
      <Route path={ROUTES.sheetTab}>
        {(params) =>
          isTab(params['tab']) ? (
            <TabBar
              active={params['tab']}
              onSelect={(next) => {
                go(sheetPath(params['id'] ?? '', next))
              }}
            />
          ) : null
        }
      </Route>
    </div>
  )
}

/**
 * Правка выборов (D62): визард, открытый на сделанных ответах.
 *
 * Ответы восстанавливает сервер из журнала — источник один (D4).
 * Драфт поправлен сразу, у персонажа за столом правка ушла мастеру:
 * в обоих случаях человек возвращается на лист и видит, что вышло.
 */
function EditScreen({
  api,
  characterId,
  onDone,
}: {
  readonly api: Api
  readonly characterId: string
  readonly onDone: () => void
}) {
  const t = useT()
  const [edit, setEdit] = useState<CreationEdit | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void api
      .creationEdit(characterId)
      .then((next) => {
        if (!cancelled) setEdit(next)
      })
      .catch(() => {
        if (!cancelled) setFailure(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId, t])

  if (failure !== null) {
    return (
      <p className="app-screen app-problems" role="alert">
        {failure}
      </p>
    )
  }
  if (edit === null) return <p className="app-screen">{t('wizard.loading')}</p>

  return (
    <CreationWizard
      api={api}
      seatId={edit.seatId}
      editing={{
        characterId,
        choices: edit.choices,
        level: edit.level,
        seated: edit.seatId !== null,
      }}
      onCreated={onDone}
      onEdited={onDone}
      onCancel={onDone}
    />
  )
}

function SheetScreen({
  api,
  characterId,
  tab,
  signedIn,
  buildTo,
  onHeading,
  onEditChoices,
  onPrint,
  onBack,
}: {
  readonly api: Api
  readonly characterId: string
  readonly tab: TabKey
  /** Вошёл аккаунтом: значит, это мастер (D8). */
  readonly signedIn: boolean
  /** Докуда доводить персонажа повышениями после создания (D60). */
  readonly buildTo: number | null
  readonly onHeading: (heading: Heading | null) => void
  readonly onEditChoices: () => void
  readonly onPrint: () => void
  readonly onBack: () => void
}) {
  return (
    <div
      role="tabpanel"
      id={`panel-${tab}`}
      aria-labelledby={`tab-${tab}`}
      tabIndex={0}
      className="app-panel"
      data-active-tab={tab}
    >
      {/* Лист читает `CharacterSheetView`, а вкладку выбирает он же:
          инвентарь, бой и заклинания приходят одной свёрткой вместе
          с величинами (D4), и два запроса за одним листом означали бы
          КД от доспеха, которого в списке ещё нет. */}
      <CharacterSheetView
        api={api}
        characterId={characterId}
        tab={tab}
        signedIn={signedIn}
        buildTo={buildTo}
        onHeading={onHeading}
        onEditChoices={onEditChoices}
        onPrint={onPrint}
        onBack={onBack}
      />
    </div>
  )
}

/**
 * Неизвестный адрес.
 *
 * Пустой экран на опечатке в ссылке выглядит как поломка приложения,
 * а это всего лишь адрес, которого нет, — и сказать об этом дешевле,
 * чем оставить человека гадать.
 */
function UnknownRoute({ onHome }: { readonly onHome: () => void }) {
  const t = useT()
  const [location] = useLocation()

  return (
    <section className="app-screen" aria-labelledby="not-found-title">
      <h2 id="not-found-title">{t('notFound.title')}</h2>
      <p>{t('notFound.text', { path: location })}</p>
      <div className="app-actions">
        <button type="button" className="app-button" onClick={onHome}>
          {t('notFound.home')}
        </button>
      </div>
    </section>
  )
}
